import { type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';

import { EntitySectionLayout } from '@/components/composed/EntitySectionLayout';
import { DateCell } from '@/components/composed/DateCell';
import { IdCell } from '@/components/composed/IdCell';
import { MaskedCell } from '@/components/composed/MaskedCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { EmptyState } from '@/components/composed/EmptyState';
import { resolveSection } from '@/lib/entity-sections';
import { providerDetailQuery } from '../api/provider-queries';
import type { ProviderId } from '@/lib/api/ids';
import { providerSections } from '../provider-sections';

/*
 * ProviderDetail — the first real consumer of the composed EntitySectionLayout.
 *
 * The route resolves the active section from the `$section` param and PUBLISHES
 * the entity sub-nav into the shell rail (via `useRailSubNav`). This
 * component owns only the domain wiring left in the content column: resolving
 * which body to render for the active section. It is navigation-mechanism-
 * agnostic — it never imports a router.
 */
export function ProviderDetail({
  providerId,
  section,
}: {
  providerId: ProviderId;
  /** Raw `$section` route param (may be missing/invalid). */
  section: string | undefined;
}) {
  const { data: provider } = useSuspenseQuery(providerDetailQuery(providerId));
  const activeSectionId =
    resolveSection(section, providerSections) ?? providerSections[0]!.id;

  return (
    <EntitySectionLayout>
      <SectionBody sectionId={activeSectionId} provider={provider} />
    </EntitySectionLayout>
  );
}

function SectionBody({
  sectionId,
  provider,
}: {
  sectionId: string;
  provider: import('@/lib/api/domains/providers').Provider;
}) {
  if (sectionId === 'profile') {
    return (
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <Field label="Name">
          <span className="font-medium">{provider.name}</span>
        </Field>
        {provider.dbaName ? (
          <Field label="DBA name">
            <span className="font-medium">{provider.dbaName}</span>
          </Field>
        ) : null}
        {provider.publicId ? (
          <Field label="Public ID">
            <IdCell value={provider.publicId} />
          </Field>
        ) : null}
        {provider.providerType ? (
          <Field label="Type">
            <span className="font-medium">{provider.providerType}</span>
          </Field>
        ) : null}
        {provider.taxId ? (
          <Field label="TIN">
            <MaskedCell value={provider.taxId} />
          </Field>
        ) : null}
        <Field label="Status">
          <StatusBadge
            label={provider.status === 'active' ? 'Active' : 'Inactive'}
            tone={provider.status === 'active' ? 'success' : 'neutral'}
          />
        </Field>
        <Field label="Created">
          <DateCell value={provider.createdAt} />
        </Field>
      </dl>
    );
  }

  // The remaining sections (disbursements, distribution-rules, reconciliation,
  // contracts, bank-accounts, disbursement-schedules) have no FE contract yet —
  // their endpoints are the entity sub-domains that land in later slices. The
  // layout + deep-link routing is fully wired now; each section fills in when
  // its data contract exists.
  const label = providerSectionLabel(sectionId) ?? sectionId.replace(/-/g, ' ');
  return (
    <EmptyState
      title={label}
      description="This section will be available in a later release."
    />
  );
}

function providerSectionLabel(id: string): string | undefined {
  return providerSections.find((s) => s.id === id)?.label;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}
